/* eslint-disable max-len */
import {
  Edit,
  SimpleForm,
  TextInput,
  useEditContext,
  useTranslate,
  AutocompleteInput,
  useUpdate,
  useUpdateMany,
  ShowButton,
  useCreate
} from "react-admin";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import "yup-phone";
import { useQuery } from "@apollo/client";

import { usePhotoUploadSubmitEdit } from "hooks/usePhotoUploadSubmitEdit";
import * as User from "providers/user";
import * as Axios from "providers/axios";
import PhoneInput from "./PhoneInput";
import CustomHeaderActions from "./CustomHeaderActions";
import ShowAndEditDetails from "./ShowAndEditDetails";
import { DefaultAvatarIcon, PersonIcon, ShowButtonIcon } from "assets/Icons";
import CustomPronounSelect from "./CustomPronounSelect";
import CustomImageInputSmall from "./CustomImageInputSmall";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import AdvancedDetails from "./AdvancedDetails";
import ShowCards from "./ShowCards";
import { correctDate } from "../utils/correctDate";
import { CustomToolbar } from "./CustomToolbar";
import {
  CardElement,
  Elements,
  useElements,
  useStripe
} from "@stripe/react-stripe-js";
import { stripePromise, CARD_ELEMENT_OPTIONS } from "stripeConfig";
import { useFormContext } from "react-hook-form";
import CustomDayAndMonthPicker from "./CustomDayAndMonthPicker";
import { isPhoneValid } from "utils/isPhoneValid";
import { GET_USER } from "../gql";
import ReminderSettings, { ReminderEnabledMap } from "./ReminderSettings";

const roleChoices = [
  { id: "SUPER", role: "SUPER" },
  { id: "OWNER", role: "OWNER" },
  { id: "MANAGER", role: "MANAGER" },
  { id: "ARTIST", role: "ARTIST" }
];

const RoleField = () => {
  const { record } = useEditContext();
  const { user, permissions } = User.useUser();
  const translate = useTranslate();

  const choiceCondition = () => {
    if (permissions.isSuperAdmin && !user.impersonate_company_id) {
      return roleChoices;
    }
    if (permissions.isOwner) {
      return roleChoices.slice(1);
    }
    return roleChoices.slice(2);
  };

  const disabledCondition = () => {
    if (permissions.isArtist || record.id === user.id) {
      return true;
    }
    return false;
  };

  if (
    !permissions.isSuperAdmin &&
    record?.joined_company_id !== permissions.companyId
  ) {
    return null;
  }
  return (
    <AutocompleteInput
      optionText={(value) => value.role}
      label={translate("users.role")}
      source="role.role"
      fullWidth
      choices={choiceCondition()}
      disabled={disabledCondition()}
      defaultValue={record.role?.role || null}
    />
  );
};

export default function UserEditContainer() {
  return (
    <Elements stripe={stripePromise}>
      <UserEdit />
    </Elements>
  );
}

export const CardComponent = ({
  setIsNewCardEmpty,
  setStripeErrorMsg
}: {
  setIsNewCardEmpty: Dispatch<SetStateAction<boolean>>;
  setStripeErrorMsg: Dispatch<SetStateAction<string>>;
}) => {
  const formContext = useFormContext();
  return (
    <CardElement
      onChange={(e) => {
        if (e?.complete && !e?.error) {
          setStripeErrorMsg("");
          formContext?.setValue("fakeDirtyFormWorkaround", "true", {
            shouldDirty: true
          });
        }
        if (e?.empty) {
          setIsNewCardEmpty(true);
          setStripeErrorMsg("");
        } else {
          setIsNewCardEmpty(false);
        }
      }}
      options={CARD_ELEMENT_OPTIONS}
    />
  );
};

function UserEdit() {
  const { id } = useParams<{ id: string }>();
  const { permissions, user } = User.useUser();
  const [edit] = useUpdate();
  const [create] = useCreate();
  const [editMany] = useUpdateMany();
  const { api } = Axios.useAxios();
  const navigate = useNavigate();
  const stripe = useStripe();
  const elements = useElements();
  const [description, setDescription] = useState<string>("");
  const [selectedDefaultCardIdState, setSelectedDefaultCardIdState] =
    useState<string>("");
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isNewCardEmpty, setIsNewCardEmpty] = useState<boolean>(true);
  const [stripeErrorMsg, setStripeErrorMsg] = useState<string>("");
  const [reminderEnabled, setReminderEnabled] = useState<ReminderEnabledMap>(
    {}
  );

  const isOwnAccount = !!user?.id && id === user.id;
  const showReminders =
    isOwnAccount || permissions.isSuperAdmin || permissions.isOwner;
  const remindersEditable = isOwnAccount || permissions.isSuperAdmin;
  const showPromo = isOwnAccount || permissions.isSuperAdmin;
  const promoEditable = showPromo;

  const { submit: submitWithPhoto } = usePhotoUploadSubmitEdit({
    redirect: window.location.pathname.includes("/dashboard/Customer")
      ? "/dashboard/Customer"
      : "/dashboard/User",
    resource: "User",
    keys: ["photo_url"],
    setIsLoading: setIsLoading
  });
  const translate = useTranslate();
  const router = useLocation();
  const resourceFromUrl = router.pathname.split("/")[2];

  const { data: customerUser, refetch } = useQuery(GET_USER, {
    variables: {
      where: { id }
    }
  });

  useEffect(() => {
    refetch();
    if (customerUser?.user?.company_descriptions?.length > 0) {
      const companyDescriptions = customerUser.user.company_descriptions.find(
        (cd: any) => cd.company_id === permissions?.companyId
      );
      setDescription(companyDescriptions?.description || "");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [customerUser?.user?.company_descriptions, permissions?.companyId]);

  useEffect(() => {
    const settings = customerUser?.user?.reminder_settings;
    if (settings?.length > 0) {
      const map: ReminderEnabledMap = {};
      settings.forEach((setting: any) => {
        map[setting.type] = !!setting.enabled;
      });
      setReminderEnabled(map);
    }
  }, [customerUser?.user?.reminder_settings]);

  const submit = async (data: any) => {
    setIsLoading(true);
    if (data.fakeDirtyFormWorkaround) {
      delete data.fakeDirtyFormWorkaround;
    }

    try {
      setIsLoading(true);

      if (stripe && elements && !isNewCardEmpty) {
        const cardElement = elements.getElement(CardElement);

        if (cardElement) {
          const { error, paymentMethod } = await stripe.createPaymentMethod({
            type: "card",
            card: cardElement,
            billing_details: {
              name: `${data?.first_name} ${data?.last_name}`,
              email: data?.email,
              phone: data?.phone_number || ""
            }
          });

          if (error) {
            setStripeErrorMsg(error?.message || "");
            setIsLoading(false);
            return;
          } else {
            try {
              await api.post("/v1/payment-methods", {
                paymentMethod: {
                  id: paymentMethod?.id,
                  brand: paymentMethod?.card?.brand,
                  exp_month: paymentMethod?.card?.exp_month,
                  exp_year: paymentMethod?.card?.exp_year,
                  last4: paymentMethod?.card?.last4
                },
                user_id: id
              });
            } catch (e) {
              setStripeErrorMsg(e?.response?.data?.message || e?.message || "");
              setIsLoading(false);
              return;
            }
          }
        }
      }
      if (selectedDefaultCardIdState) {
        await edit("StripePaymentMethod", {
          id: selectedDefaultCardIdState,
          data: { is_default: true }
        });
        await editMany("StripePaymentMethod", {
          ids: customerUser?.user.StripePaymentMethods.filter(
            (card: any) => selectedDefaultCardIdState !== card.id
          ).map((card: any) => card.id),
          data: { is_default: false }
        });
      }

      // persist the reminder-setting toggles the current admin is allowed to change
      const editableReminderTypes: string[] = [];
      if (remindersEditable) editableReminderTypes.push("EMAIL", "SMS");
      if (promoEditable) editableReminderTypes.push("EMAIL_PROMO", "SMS_PROMO");

      const existingReminderSettings = customerUser?.user?.reminder_settings;
      if (
        editableReminderTypes.length > 0 &&
        Array.isArray(existingReminderSettings)
      ) {
        for (const type of editableReminderTypes) {
          const existing = existingReminderSettings.find(
            (rs: any) => rs.type === type
          );
          const nextEnabled = !!reminderEnabled[type];

          if (existing) {
            if (existing.enabled !== nextEnabled) {
              await edit("ReminderSetting", {
                id: existing.id,
                data: { enabled: nextEnabled }
              });
            }
          } else if (nextEnabled) {
            await create("ReminderSetting", {
              data: {
                type,
                enabled: nextEnabled,
                value: type.startsWith("EMAIL")
                  ? data.email || ""
                  : data.phone_number || "",
                user: { id }
              }
            });
          }
        }
      }

      // @the-real-adammork this is a workaround for the role field not being able to be null
      if (data.role.id) {
        await edit(
          "Role",
          { id: data.role.id, data: { role: data.role.role } },
          {
            onSuccess: async () => {
              await refetch();
              navigate(
                window.location.pathname.includes("/dashboard/Customer")
                  ? "/dashboard/Customer"
                  : "/dashboard/User"
              );
            }
          }
        );
      }

      const payload = {
        ...data
      };

      if (data.description) {
        const companyDescriptions = data.company_descriptions?.find(
          (cd: any) => cd.company_id === permissions?.companyId
        );
        if (companyDescriptions) {
          await edit("CompanyUserDescription", {
            id: companyDescriptions.id,
            data: {
              description: data.description
            }
          });
        } else {
          await create("CompanyUserDescription", {
            data: {
              description: data.description,
              user: { id },
              company: { id: permissions?.companyId }
            }
          });
        }

        delete payload.description;
      }

      if (data.phone_number || data.email) {
        const phoneReminder = data.reminder_settings?.find(
          (rs: any) => rs.type === "SMS"
        );
        const emailReminder = data.reminder_settings?.find(
          (rs: any) => rs.type === "EMAIL"
        );

        if (phoneReminder) {
          await edit("ReminderSetting", {
            id: phoneReminder.id,
            data: {
              value: data.phone_number
            }
          });
        }

        if (emailReminder) {
          await edit("ReminderSetting", {
            id: emailReminder.id,
            data: {
              value: data.email
            }
          });
        }
      }

      if (!payload.role.create) {
        delete payload.role;
      }

      if (data.birthday) {
        payload.birthday = correctDate(data.birthday);
      } else {
        payload.birthday = "";
      }

      const customOnSuccess = async (customerUser: any) => {
        if (customerUser?.email !== data.email) {
          await api.post(
            `${process.env.REACT_APP_API_CORE_URL}/v1/users/edit-email`,
            { id: customerUser.id }
          );
        }

        setIsLoading(false);
        navigate(
          window.location.pathname.includes("/dashboard/Customer")
            ? "/dashboard/Customer"
            : "/dashboard/User"
        );
      };

      submitWithPhoto(payload, customOnSuccess);
    } catch (error) {
      throw new Error(error);
    }
  };

  return (
    // This resource should always be "User" not {"Customer" || "User"} because in DB we only have
    // the "User" model, we use "Customer" as route and page name but it still fetches users.
    <Edit
      resource="User"
      actions={<CustomHeaderActions title={translate("users.customer")} />}
    >
      <SimpleForm
        onSubmit={submit}
        resolver={yupResolver(validationSchema)}
        toolbar={<CustomToolbar isLoading={isLoading} />}
      >
        <ShowAndEditDetails
          title={translate("users.customer_details")}
          view="edit"
          icon={<PersonIcon />}
          customButtons={
            <ShowButton
              resource={resourceFromUrl}
              icon={<ShowButtonIcon />}
              sx={{
                display: "flex",
                justifyContent: "center",
                alignItems: "center",
                height: "36px",
                backgroundColor: "#FAF9F7",
                color: "rgba(48, 47, 46, 0.95)",
                fontSize: "14px",
                lineHeight: "18px",
                fontWeight: 500,
                borderRadius: "18px",
                padding: "0px 16px",
                border: "1px solid rgba(48, 47, 46, 0.23)",
                textTransform: "none",
                "&:hover": {
                  backgroundColor: "rgba(48, 47, 46, 0.04)"
                }
              }}
            />
          }
        />
        <CustomImageInputSmall
          source="photo_url"
          borderRadius="20px"
          buttonText={translate("common.set_photo")}
          bg="#B0ACA6"
          placeholder={<DefaultAvatarIcon />}
        />
        <RoleField />
        <TextInput
          source="first_name"
          label={translate("users.first_name")}
          fullWidth
        />
        <TextInput
          source="last_name"
          label={translate("users.last_name")}
          fullWidth
        />
        <TextInput
          disabled={!!customerUser?.user?.email}
          source="email"
          fullWidth
        />
        {!!customerUser?.user?.email && (
          <div className="text-[14px] font-normal font-['Inter'] mt-[-16px] mb-2">
            {translate("users.change_email_contact")}{" "}
            <a
              href={`mailto:info@pointmentapp.com?subject=Email Change Support Request for ${customerUser?.user?.email}`}
              className="text-[14px] text-mono-link font-normal font-['Inter'] hover:underline cursor-pointer"
              // style={{
              //   color: "rgba(48, 47, 46, 0.95)",
              // }}
            >
              info@pointmentapp.com
            </a>
          </div>
        )}
        <PhoneInput source="phone_number" fullWidth />
        <CustomDayAndMonthPicker label={translate("users.birthday")} />
        <CustomPronounSelect
          source="pronoun"
          label={translate("users.pronoun")}
          className="w-full"
        />
        <AdvancedDetails>
          <div>
            <ReminderSettings
              enabledMap={reminderEnabled}
              setEnabledMap={setReminderEnabled}
              showReminders={showReminders}
              remindersEditable={remindersEditable}
              showPromo={showPromo}
              promoEditable={promoEditable}
            />
            <TextInput
              source="description"
              defaultValue={description}
              label={translate("users.description")}
              fullWidth
            />
            <CardComponent
              setIsNewCardEmpty={setIsNewCardEmpty}
              setStripeErrorMsg={setStripeErrorMsg}
            />
            {stripeErrorMsg && (
              <span className="text-error text-[12px]">{stripeErrorMsg}</span>
            )}
            <ShowCards
              user={customerUser?.user}
              selectedDefaultCardIdState={selectedDefaultCardIdState}
              setSelectedDefaultCardIdState={setSelectedDefaultCardIdState}
              refetch={refetch}
              editMode
            />
          </div>
        </AdvancedDetails>
      </SimpleForm>
    </Edit>
  );
}

const validationSchema = yup
  .object()
  .shape({
    first_name: yup.string().required("First name required."),
    last_name: yup.string().required("Last name required."),
    email: yup.string().email("Incorrectly formatted email.").nullable(),
    photo_url: yup.object().nullable(),
    phone_number: yup
      .string()
      .test("phone", "Invalid phone number", (value) => {
        return isPhoneValid(value);
      }),
    birthday: yup.date().nullable(),
    pronoun: yup.string().nullable()
  })
  .required();
